import { useEffect, useRef, useState } from 'react';

/**
 * Обратный отсчёт, который идёт ТОЛЬКО пока вкладка открыта.
 *
 * ⚠️ ЗАЧЕМ. Заказчик 07.09.2026: «таймер должен считать только тогда, когда
 * ученик находится во вкладке спринта или мока». Требование справедливое:
 * человек отвлёкся на звонок, вернулся — а экзамен сдался сам, и в результате
 * стоят нули. Наказание за то, что зазвонил телефон.
 *
 * ⚠️ ПОЧЕМУ НЕЛЬЗЯ ПРОСТО ВЫЧИТАТЬ СЕКУНДЫ ПО ТИКУ. `setInterval` в скрытой
 * вкладке браузер придушивает — раз в минуту вместо раза в секунду, — но НЕ
 * останавливает. То есть наивный счётчик и так «врёт», причём непредсказуемо:
 * на одном телефоне отсчёт замедлится, на другом почти встанет. Считать надо по
 * ЧАСАМ и вычитать только то время, что вкладка была visible.
 *
 * ⚠️ ЧТО СЧИТАЕТСЯ «ВКЛАДКА ОТКРЫТА». Только `visibilitychange`: свернули
 * браузер, переключили вкладку, погасили экран телефона. Потерю фокуса окна
 * (`blur`) намеренно НЕ учитываем — иначе таймер вставал бы от клика по другому
 * окну на втором мониторе, а человек при этом смотрит в экзамен.
 */
export function useActiveTimer(seconds: number, running: boolean, onExpire: () => void) {
  const [remaining, setRemaining] = useState(seconds);

  const left = useRef(seconds);
  const fired = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  // Пересев на новое значение (началась попытка, восстановили прогресс).
  useEffect(() => {
    left.current = seconds;
    fired.current = false;
    setRemaining(seconds);
  }, [seconds]);

  useEffect(() => {
    if (!running) return;

    let lastTick = Date.now();
    let visible = document.visibilityState === 'visible';

    const tick = () => {
      const now = Date.now();
      const elapsed = (now - lastTick) / 1000;
      lastTick = now;

      // ⚠️ ВРЕМЯ ЗА ПРЕДЕЛАМИ ВКЛАДКИ НЕ ВЫЧИТАЕМ ВОВСЕ. Это и есть требование:
      // отвернулся — часы стоят.
      if (!visible) return;

      left.current = Math.max(0, left.current - elapsed);
      setRemaining(Math.ceil(left.current));

      if (left.current <= 0 && !fired.current) {
        fired.current = true;
        onExpireRef.current();
      }
    };

    const onVisibility = () => {
      // При возврате сначала сдвигаем точку отсчёта, и только потом отмечаем
      // вкладку видимой: иначе первый же тик вычтет всё время отсутствия.
      lastTick = Date.now();
      visible = document.visibilityState === 'visible';
    };

    document.addEventListener('visibilitychange', onVisibility);
    const t = setInterval(tick, 1000);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [running]);

  return remaining;
}
